/**
 * Per-project model config stored as JSON on `Project.modelConfig`, resolved by
 * the coordinator each prompt. Picks which model per use case; `openRouterEnabled`
 * is a separate transport switch that is opt-in (default off) until the OpenRouter
 * snapshot + funded account land — on routes through OpenRouter and enables
 * experiments; off (the default) routes Claude direct to Anthropic (GLM degrades
 * to Sonnet).
 */
import { z } from "zod";

// Named subagents (agents/<name>.md) that can carry their own model.
export const OPENCODE_SUBAGENT_NAMES = [
  "figma",
  "replicate",
  "build-from-direction",
  "seo-researcher",
  "support",
  "write-business-md",
  "write-design-md",
] as const;

// Upstream brand behind each choice, for logos/grouping in the UI.
export const agentModelProviderSchema = z
  .enum(["anthropic", "google", "zai", "moonshot", "openai", "deepseek"])
  .describe("AgentModelProvider");

// NOTE (Kevin, 2026-07-25): Choices use OpenRouter when enabled and their direct-provider route otherwise.
export const agentModelChoiceSchema = z
  .enum([
    "sonnet-5",
    "haiku-4-5",
    "opus-5",
    "opus-4-8",
    "gemini-3.6-flash",
    "gemini-3.7-flash",
    "glm-4.6",
    "glm-5.2",
    "glm-5.3",
    "kimi-3",
    "gpt-5.6-luna",
    "gpt-5.6-terra",
    "gpt-5.6-sol",
    "deepseek-v4-flash",
  ])
  .describe("AgentModelChoice");

// Everything that independently selects a model: session types + "agent:" subagents.
// (Completeness vs OpencodeSessionType/OPENCODE_SUBAGENT_NAMES is enforced at the coordinator call sites.)
export const agentModelUseCaseSchema = z
  .enum([
    "main",
    "subagent",
    "selectModeInlineEdit",
    "publish",
    "gitConflict",
    "duplicatePage",
    "deletePage",
    "analyticsScriptDetection",
    "filesystemSession",
    "scheduledTask",
    "memoryCuration",
    "siteReport",
    "agent:figma",
    "agent:replicate",
    "agent:build-from-direction",
    "agent:seo-researcher",
    "agent:support",
    "agent:write-business-md",
    "agent:write-design-md",
  ])
  .describe("AgentModelUseCase");

export const agentModelConfigSchema = z
  .object({
    useDefaultModels: z.boolean(),
    // Partial so configs predating a new use case still parse; resolver fills gaps per-key.
    models: z
      .partialRecord(agentModelUseCaseSchema, agentModelChoiceSchema)
      .optional(),
    // Transport switch: absent/false = direct Anthropic (default, opt-in OpenRouter); true = OpenRouter + experiments.
    openRouterEnabled: z.boolean().optional(),
  })
  .describe("AgentModelConfig");

export function getDefaultAgentModelConfig(): z.infer<
  typeof agentModelConfigSchema
> {
  return { useDefaultModels: true };
}
